import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, AlertTriangle, ArrowRight, MessageSquare, Upload, Palette, BarChart3, Globe } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import SEO from "../components/SEO";
import LandingNav from "../components/Layout/LandingNav";
import LandingFooter from "../components/Layout/LandingFooter";
import BrowserFrame from "../components/marketing/BrowserFrame";
import ShowcaseSlider from "../components/marketing/ShowcaseSlider";
import { PLATFORM_PAGES, PRODUCT_HIGHLIGHTS, APP_MARKET_SLIDES, getPlatformPage } from "../config/platformPages";
import { TRIAL_DAYS, PRICE_RANGE } from "../config/plans";
import { trackCta, track, EVENTS } from "../utils/analytics";
import wixChannels from "../assets/wix-channels.webp";
import wixAnswers from "../assets/wix-answers.webp";
import wixLive from "../assets/wix-live.webp";
import wixKnowledge from "../assets/wix-knowledge.webp";
import wixAppearance from "../assets/wix-appearance.webp";
import wixAnalytics from "../assets/wix-analytics.webp";
import dashboardImage from "../assets/dashboard.png";
import dashboardImageMobile from "../assets/dashboard-mobile.png";
import heroImage from "../assets/hero.webp";
import heroImageMobile from "../assets/hero-mobile.webp";

// One template rendering every /ai-chatbot-for-<platform> page from
// src/config/platformPages.js.
//
// Layout deliberately follows the homepage: centred hero text with ONE wide visual
// underneath. An earlier version used a split hero — copy in a left column beside a tall
// stack of two browser frames — which left the text floating in whitespace against a much
// taller right column. Keep the hero single-column, and keep one visual per section.
const HIGHLIGHT_ICONS = {
    upload: Upload,
    palette: Palette,
    message: MessageSquare,
    chart: BarChart3,
    globe: Globe,
};

const SHOWCASE_IMAGES = {
    channels: wixChannels,
    answers: wixAnswers,
    live: wixLive,
    knowledge: wixKnowledge,
    appearance: wixAppearance,
    analytics: wixAnalytics,
};

function PlatformPage({ slug }) {
    const { theme } = useTheme();
    const d = theme === "dark";
    const { user } = useAuth();
    const [openFaq, setOpenFaq] = useState(null);
    const page = getPlatformPage(slug);

    if (!page) return null;

    const others = PLATFORM_PAGES.filter((p) => p.slug !== slug);
    const isAppInstall = page.installType === "app";

    // Wix shows the real App Market carousel. Shopify and WordPress have no marketplace
    // listing, so they get one honest screenshot of the dashboard instead. The in-app CSS
    // mockups were tried here and looked broken: they are sized for the small preview
    // cards on the homepage, so at full slider width the content clung to the top and left
    // most of the frame empty.
    const slides = APP_MARKET_SLIDES.map((c, i) => ({
        key: c.key,
        label: c.label,
        node: (
            <img
                src={SHOWCASE_IMAGES[c.key]}
                alt={c.alt}
                loading={i === 0 ? "eager" : "lazy"}
                width="1600"
                height="1200"
                className="w-full h-auto block"
                draggable="false"
            />
        ),
    }));

    const openDemoChat = () => {
        track(EVENTS.DEMO_OPENED, { page: `platform_${page.slug}` });
        const launcher = document.querySelector('[id^="paperbrain-chat-button-"]');
        if (launcher) {
            launcher.click();
            return;
        }
        window.location.href = "mailto:support@paperbrainz.com";
    };

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Home", item: "https://paperbrainz.com/" },
                    { "@type": "ListItem", position: 2, name: page.title, item: `https://paperbrainz.com${page.path}` },
                ],
            },
            {
                "@type": "HowTo",
                name: `How to add an AI chatbot to your ${page.platform} site`,
                step: page.install.map((text, i) => ({ "@type": "HowToStep", position: i + 1, text })),
            },
            {
                "@type": "FAQPage",
                mainEntity: page.faq.map((item) => ({
                    "@type": "Question",
                    name: item.q,
                    acceptedAnswer: { "@type": "Answer", text: item.a },
                })),
            },
        ],
    };

    const h2 = `text-4xl md:text-5xl font-semibold tracking-tight [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`;
    const sub = `text-lg md:text-xl font-light ${d ? "text-gray-500" : "text-gray-500"}`;

    const primaryCta = isAppInstall ? (
        <a
            href={page.appMarketUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta(`platform_${page.slug}`, "open_app_market")}
            className={`px-7 py-3.5 rounded-full font-medium text-[15px] inline-flex items-center justify-center gap-2 transition-colors ${d ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-black"}`}
        >
            Add to your {page.platform} site
            <ArrowRight className="w-4 h-4" />
        </a>
    ) : (
        <Link
            to={user ? "/dashboard" : "/register"}
            onClick={() => trackCta(`platform_${page.slug}`, user ? "go_to_dashboard" : "start_trial")}
            className={`px-7 py-3.5 rounded-full font-medium text-[15px] text-center transition-colors ${d ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-black"}`}
        >
            {user ? "Go to Dashboard" : `Start ${TRIAL_DAYS}-day free trial`}
        </Link>
    );

    return (
        <div className={`min-h-screen flex flex-col ${d ? "bg-[#0a0a0a]" : "bg-white"}`}>
            <SEO title={page.title} description={page.metaDescription} path={page.path} jsonLd={jsonLd} />
            <LandingNav />

            <div className="flex-1">
                {/* Hero — centred text, one wide visual below. Same shape as the homepage. */}
                <section className="px-4 sm:px-6 lg:px-8 pt-32 pb-20">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center max-w-3xl mx-auto">
                            <p className={`text-sm font-medium tracking-wide mb-6 ${d ? "text-gray-400" : "text-gray-500"}`}>
                                PaperBrainz for {page.platform}
                            </p>
                            <h1 className={`text-[44px] sm:text-5xl md:text-6xl font-semibold mb-7 leading-[1.05] tracking-[-0.02em] [text-wrap:balance] ${d ? "text-white" : "text-gray-900"}`}>
                                {page.heading}
                            </h1>
                            <p className={`text-lg md:text-xl mb-10 leading-relaxed max-w-2xl mx-auto font-light ${d ? "text-gray-400" : "text-gray-600"}`}>
                                {page.intro}
                            </p>

                            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                {primaryCta}
                                <button
                                    onClick={() => {
                                        trackCta(`platform_${page.slug}`, "try_demo");
                                        openDemoChat();
                                    }}
                                    className={`px-7 py-3.5 rounded-full font-medium text-[15px] border inline-flex items-center justify-center gap-2 transition-colors ${d ? "text-white border-white/15 hover:bg-white/[0.04]" : "text-gray-900 border-gray-300 hover:bg-gray-50"}`}
                                >
                                    <MessageSquare className="w-4 h-4" />
                                    Ask our chatbot
                                </button>
                            </div>

                            <p className={`mt-6 text-sm ${d ? "text-gray-500" : "text-gray-500"}`}>
                                {isAppInstall
                                    ? `Installs in one click from the ${page.platform} App Market · No code`
                                    : `No credit card required · Plans from $${PRICE_RANGE.low}/month`}
                            </p>
                        </div>

                        <div className="mt-16 max-w-4xl mx-auto">
                            <BrowserFrame d={d} url={page.demoUrl}>
                                <picture>
                                    <source media="(min-width: 640px)" srcSet={heroImage} />
                                    <img
                                        src={heroImageMobile}
                                        alt={`The PaperBrainz chat widget answering a visitor question on a ${page.platform} site`}
                                        className="w-full h-auto block"
                                        draggable="false"
                                    />
                                </picture>
                            </BrowserFrame>
                        </div>
                    </div>
                </section>

                {/* Problem */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className={`${h2} mb-6`}>Why {page.platform} owners need this</h2>
                        {page.problem.map((para, i) => (
                            <p key={i} className={`text-lg font-light leading-relaxed mb-4 ${d ? "text-gray-400" : "text-gray-600"}`}>
                                {para}
                            </p>
                        ))}
                    </div>
                </section>

                {/* Showcase — the App Market listing cards, one per row */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <h2 className={`${h2} mb-4`}>See it in action</h2>
                            <p className={sub}>
                                {isAppInstall
                                    ? `The same walkthrough as the ${page.platform} App Market listing.`
                                    : "Train it, style it and read every conversation from one dashboard."}
                            </p>
                        </div>
                        {isAppInstall ? (
                            <ShowcaseSlider d={d} slides={slides} ariaLabel={`PaperBrainz on ${page.platform}`} />
                        ) : (
                            <BrowserFrame d={d} url="paperbrainz.com/dashboard">
                                <picture>
                                    <source media="(min-width: 640px)" srcSet={dashboardImage} />
                                    <img
                                        src={dashboardImageMobile}
                                        alt="The PaperBrainz dashboard showing conversation metrics and recent activity"
                                        loading="lazy"
                                        className="w-full aspect-[3/4] sm:aspect-[16/9] object-cover object-top block"
                                        draggable="false"
                                    />
                                </picture>
                            </BrowserFrame>
                        )}
                    </div>
                </section>

                {/* Setup — compact numbered cards, no oversized mockups */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <h2 className={`${h2} mb-4`}>How to set it up on {page.platform}</h2>
                            <p className={sub}>
                                {isAppInstall
                                    ? "Four steps, none of them involving code. Most people are live the same day."
                                    : "Four steps. Most people are live the same day."}
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            {page.install.map((step, i) => (
                                <div
                                    key={i}
                                    className={`rounded-[20px] border p-6 sm:p-7 ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                                >
                                    <span className={`text-4xl font-extralight tracking-tight ${d ? "text-white/15" : "text-black/15"}`}>
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <p className={`font-light leading-relaxed mt-2 ${d ? "text-gray-400" : "text-gray-600"}`}>{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Features */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-5xl mx-auto">
                        <div className="text-center mb-14 max-w-2xl mx-auto">
                            <h2 className={`${h2} mb-4`}>What you get</h2>
                            <p className={sub}>Included on every plan unless the pricing page says otherwise.</p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                            {PRODUCT_HIGHLIGHTS.map((f) => {
                                const Icon = HIGHLIGHT_ICONS[f.icon] || Check;
                                return (
                                    <div
                                        key={f.title}
                                        className={`rounded-[20px] border p-6 sm:p-7 ${d ? "bg-white/[0.02] border-white/[0.06]" : "bg-gray-50 border-gray-200"}`}
                                    >
                                        <div className={`w-10 h-10 rounded-[12px] flex items-center justify-center mb-4 ${d ? "bg-white/[0.05]" : "bg-white border border-gray-200"}`}>
                                            <Icon className={`w-5 h-5 ${d ? "text-gray-300" : "text-gray-700"}`} strokeWidth={1.75} />
                                        </div>
                                        <h3 className={`text-base font-semibold mb-2 ${d ? "text-white" : "text-gray-900"}`}>{f.title}</h3>
                                        <p className={`text-sm font-light leading-relaxed ${d ? "text-gray-400" : "text-gray-600"}`}>{f.desc}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Limitations */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-3xl mx-auto">
                        <div className={`rounded-[20px] border p-7 sm:p-9 ${d ? "border-white/[0.08] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}>
                            <div className="flex items-center gap-2.5 mb-5">
                                <AlertTriangle className={`w-5 h-5 ${d ? "text-gray-400" : "text-gray-500"}`} />
                                <h2 className={`text-xl font-semibold ${d ? "text-white" : "text-gray-900"}`}>
                                    What it does not do on {page.platform}
                                </h2>
                            </div>
                            <ul className="space-y-4">
                                {page.limitations.map((item, i) => (
                                    <li key={i} className={`font-light leading-relaxed ${d ? "text-gray-400" : "text-gray-600"}`}>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* FAQ */}
                <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24">
                    <div className="max-w-2xl mx-auto">
                        <h2 className={`${h2} text-center mb-12`}>{page.platform} questions</h2>
                        <div className="space-y-3">
                            {page.faq.map((faq, i) => (
                                <div key={faq.q} className={`rounded-[16px] border overflow-hidden ${d ? "border-white/[0.06]" : "border-gray-200"}`}>
                                    <button
                                        onClick={() => {
                                            if (openFaq !== i) track(EVENTS.FAQ_OPEN, { question_index: i + 1, page: `platform_${page.slug}` });
                                            setOpenFaq(openFaq === i ? null : i);
                                        }}
                                        className="w-full flex items-center justify-between p-5 text-left"
                                    >
                                        <span className={`font-medium pr-4 text-[15px] ${d ? "text-white" : "text-gray-900"}`}>{faq.q}</span>
                                        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""} ${d ? "text-gray-600" : "text-gray-500"}`} />
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-200 ${openFaq === i ? "max-h-96 pb-5" : "max-h-0"}`}>
                                        <p className={`px-5 text-sm leading-relaxed font-light ${d ? "text-gray-500" : "text-gray-500"}`}>{faq.a}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Final CTA */}
                <section className="px-4 sm:px-6 lg:px-8 pb-24">
                    <div className="max-w-3xl mx-auto text-center">
                        <h2 className={`${h2} mb-5`}>Put it on your {page.platform} site today</h2>
                        <p className={`${sub} mb-9`}>
                            {isAppInstall
                                ? `One click from the ${page.platform} App Market. No code, and you can remove it just as easily.`
                                : `${TRIAL_DAYS} days free on Basic. No credit card. Cancel from your dashboard whenever you like.`}
                        </p>
                        <div className="flex justify-center">{primaryCta}</div>

                        <div className={`mt-14 pt-8 border-t text-sm font-light ${d ? "border-white/[0.06] text-gray-500" : "border-gray-200 text-gray-500"}`}>
                            <p className="mb-3">Using something else?</p>
                            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2">
                                {others.map((o) => (
                                    <Link key={o.slug} to={o.path} className="underline hover:no-underline">
                                        Chatbot for {o.platform}
                                    </Link>
                                ))}
                                <Link to="/pricing" className="underline hover:no-underline">Pricing</Link>
                                <Link to="/security" className="underline hover:no-underline">Security</Link>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <LandingFooter />
        </div>
    );
}

export default PlatformPage;
